import { BUILD_ID } from "@/generated/buildId";
import { decideReload } from "@/utils/versionDecision";

// Stale-bundle auto-reload. A long-lived browser tab keeps running whatever JS
// it loaded; after a deploy that tab is stale and its API calls can fail in ways
// the old code doesn't handle (e.g. admin auth after the enforce cutover). This
// polls /version.json (regenerated each build with the same id baked into the
// bundle) and reloads the tab onto the fresh bundle when they diverge.
//
// Safety:
//  - Never reloads while a text input / textarea / contentEditable is focused
//    (don't interrupt typing); defers until it's safe.
//  - A sessionStorage guard reloads at most once per target build id, so a
//    misconfigured version.json can never cause a reload loop.
//  - All failures (offline, non-JSON, missing file) are swallowed → no-op.

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const GUARD_KEY = "__sz_reloaded_for_build";
let started = false;
let pendingBuild = null;

function isSafeToReload() {
  const el = document.activeElement;
  if (!el) return true;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return false;
  if (el.isContentEditable) return false;
  return true;
}

function reloadOnce(serverBuild) {
  try {
    if (sessionStorage.getItem(GUARD_KEY) === serverBuild) return; // already tried
    sessionStorage.setItem(GUARD_KEY, serverBuild);
  } catch (_) {
    /* sessionStorage unavailable — still reload, just without the loop guard */
  }
  window.location.reload();
}

function alreadyReloadedFor() {
  try {
    return sessionStorage.getItem(GUARD_KEY);
  } catch (_) {
    return null;
  }
}

async function checkVersion() {
  try {
    const resp = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!resp.ok) return;
    const data = await resp.json();
    const serverBuild = data && data.buildId;

    const decision = decideReload({
      bakedId: BUILD_ID,
      serverId: serverBuild,
      isSafe: isSafeToReload(),
      alreadyReloadedFor: alreadyReloadedFor(),
    });
    if (decision === "reload") reloadOnce(serverBuild);
    else if (decision === "defer") pendingBuild = serverBuild;
  } catch (_) {
    /* offline or version.json not served as JSON — ignore */
  }
}

export function startVersionCheck() {
  if (started || typeof window === "undefined") return;
  started = true;

  // Re-check when the user returns to a backgrounded tab (the most common stale
  // case) and periodically for tabs left open and in use.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkVersion();
  });
  window.addEventListener("focus", checkVersion);
  setInterval(checkVersion, CHECK_INTERVAL_MS);

  // If a reload was deferred because an input was focused, do it once that input
  // loses focus (capture phase catches blur on any element).
  document.addEventListener(
    "blur",
    () => {
      if (pendingBuild && isSafeToReload()) {
        const b = pendingBuild;
        pendingBuild = null;
        reloadOnce(b);
      }
    },
    true
  );

  setTimeout(checkVersion, 3000); // initial check shortly after load
}
